import type {
  MissionProgressSchema,
  PublicCampaignSchema,
  TaskSchema,
} from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import type { Mission, MissionTask, MissionsCampaign } from "./types";
import { parseProgressValue } from "./types";

/**
 * Fold a task list's structural rows with the player's live task state.
 * The structural stream serves tasks WITHOUT ids (invariant: per-player fields
 * null there), so the two sides join on `order`, which both carry. `scope` prefixes the
 * row ids (the campaign key plus, for a mission, its id); the chase module folds its targets
 * through the same function (#4041), so the two never diverge on the join.
 */
export function foldTasks(
  scope: string,
  structural: TaskSchema[],
  live: TaskSchema[] | undefined
): MissionTask[] {
  // Bucket live states per order instead of a single-slot map: duplicate
  // `order` values are a misconfiguration the backend doesn't forbid, and a
  // plain Map would silently drop all but the last duplicate's state.
  const liveByOrder = new Map<number, TaskSchema[]>();
  for (const t of live ?? []) {
    const bucket = liveByOrder.get(t.order);
    if (bucket) bucket.push(t);
    else liveByOrder.set(t.order, [t]);
  }
  return [...structural]
    .sort((a, b) => a.order - b.order)
    .map((task, index) => {
      const state = liveByOrder.get(task.order)?.shift();
      return {
        // Content-derived id (order), with the sorted index as a tiebreaker so
        // duplicate orders can't collide as React keys.
        id: `${scope}:${task.order}:${index}`,
        order: task.order,
        kind: task.kind,
        title: task.title,
        prizes: task.prizes ?? [],
        isActive: state?.is_active ?? false,
        isCompleted: state?.is_completed ?? false,
        progress: state?.progress ?? null,
      };
    });
}

/**
 * Fold one campaign's structural missions with the player's live per-mission state.
 *
 * Extracted from `assembleMissions` so the sprint-mission module can reuse it (#3867): a
 * Sprint Mission has exactly one mission but its tasks and completion state work identically,
 * and duplicating the `completedByRounds` reasoning below in two modules is how the two would
 * quietly diverge.
 */
export function assembleMissionRows(
  key: CampaignKey,
  campaign: PublicCampaignSchema,
  progressRows: MissionProgressSchema[]
): Mission[] {
  const progressById = new Map(progressRows.map((p) => [p.id, p]));

  // Structural array order IS the ladder order (backend orders by mission
  // `order`); `position` is the player-facing 1-based rung number.
  return (campaign.missions ?? []).map((m, index) => {
    const live = progressById.get(m.id);
    // `is_completed` and the repetition counter come from two separate
    // backend caches and can briefly disagree — a mission with every round
    // done (`current >= total`) but a stale `is_completed: false` would
    // otherwise wedge the ladder open on its last rung (no banner, wrong
    // "current mission"). The rounds counter counts COMPLETED repetitions,
    // so deriving completion from it is safe.
    const roundsDone = parseProgressValue(live?.progress.current);
    const roundsTotal = parseProgressValue(live?.progress.total);
    const completedByRounds =
      roundsDone !== null &&
      roundsTotal !== null &&
      roundsTotal >= 1 &&
      roundsDone >= roundsTotal;
    return {
      id: m.id,
      position: index + 1,
      prizes: m.prizes,
      keepOrder: m.keep_order ?? false,
      tasks: foldTasks(`${key}:${m.id}`, m.tasks, live?.tasks),
      isActive: live?.is_active ?? false,
      isCompleted: (live?.is_completed ?? false) || completedByRounds,
      progress: live?.progress ?? null,
    };
  });
}

/**
 * Join the streams into the rendered `MissionsCampaign[]`. Pure — the store calls it (via
 * `derive.ts`) after any relevant stream updates.
 *
 * An MI campaign is included as soon as its `campaigns` tile arrives (so the go-live / opt-in
 * tile is renderable) — before `mission_progress` lands its missions simply carry no per-player
 * state. `mission_progress` deliberately renders NO prizes (`prizes: null` there means "merge
 * from campaigns", unlike `[]` on the structural side which means "genuinely none") — prize
 * data therefore always comes from the structural mission/task.
 */
export function assembleMissions(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  missionProgressById: Record<CampaignKey, MissionProgressSchema[]>
): MissionsCampaign[] {
  const result: MissionsCampaign[] = [];

  for (const campaign of Object.values(campaignsById)) {
    // `subtype` is what splits the two mission modules: a pooled campaign is "SM" and belongs to
    // the sprint-mission module. `resolveCurrentCampaign` takes the FIRST module that matches,
    // so if both claimed a campaign it would render as a standard ladder, or twice.
    if (campaign.type !== "MI" || campaign.subtype !== "MI") continue;

    const key = campaignKey(campaign);
    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;
    const missions = assembleMissionRows(
      key,
      campaign,
      missionProgressById[key] ?? []
    );

    result.push({
      campaign,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : { value: 0, currency: "" },
      missions,
      eligible_games: games,
      eligible_games_count: count,
    });
  }

  return result;
}
